import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth'
import { api, type SessionItem, type Userbot } from '@/lib/api'
import { ConnectUserbotModal } from '@/components/ConnectUserbotModal'
import { Select } from '@/components/Select'

const COMMON_TIMEZONES = [
  'UTC',
  'Europe/Kaliningrad',
  'Europe/Moscow',
  'Asia/Yekaterinburg',
  'Asia/Novosibirsk',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Vladivostok',
  'Asia/Bangkok',
  'Asia/Dubai',
  'Europe/Kyiv',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
]

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 2) return 'только что'
  if (mins < 60) return `${mins} мин. назад`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} ч. назад`
  const days = Math.floor(hours / 24)
  return `${days} д. назад`
}

export function SettingsPage() {
  const { user, refresh } = useAuth()
  const queryClient = useQueryClient()

  // Fetch me for timezone (may differ from auth context if stale)
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.getMe(),
  })

  // Active sessions
  const sessionsQuery = useQuery({
    queryKey: ['auth', 'sessions'],
    queryFn: () => api.getSessions(),
  })

  const logoutAllMutation = useMutation({
    mutationFn: () => api.logoutAll(),
    onSuccess: () => {
      window.location.replace('/login')
    },
  })

  const revokeSessionMutation = useMutation({
    mutationFn: (publicId: string) => api.revokeSession(publicId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] })
    },
  })

  // Userbot list
  const userbotsQuery = useQuery({
    queryKey: ['me', 'userbots'],
    queryFn: () => api.getUserbots(),
  })

  const [selectingId, setSelectingId] = useState<number | null>(null)
  const [connectOpen, setConnectOpen] = useState(false)

  const selectUserbotMutation = useMutation({
    mutationFn: (id: number) => api.selectUserbot(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['me', 'userbots'] })
      void queryClient.invalidateQueries({ queryKey: ['chat', 'dialogs'] })
      void queryClient.invalidateQueries({ queryKey: ['chat', 'sources'] })
      setSelectingId(null)
    },
    onError: () => {
      setSelectingId(null)
    },
  })

  function handleSelectUserbot(ub: Userbot) {
    if (ub.is_active || selectUserbotMutation.isPending) return
    setSelectingId(ub.id)
    selectUserbotMutation.mutate(ub.id)
  }

  const currentTz = meQuery.data?.timezone ?? user?.timezone ?? 'UTC'
  const activeOrg = (
    meQuery.data?.organizations ?? user?.organizations ?? []
  ).find((o) => o.id === (meQuery.data?.active_organization_id ?? user?.active_organization_id))
  const [orgName, setOrgName] = useState('')
  const [orgSyncedId, setOrgSyncedId] = useState<number | null>(null)
  useEffect(() => {
    if (activeOrg && orgSyncedId !== activeOrg.id) {
      setOrgName(activeOrg.name)
      setOrgSyncedId(activeOrg.id)
    }
  }, [activeOrg, orgSyncedId])

  const orgMutation = useMutation({
    mutationFn: () => {
      if (!activeOrg) throw new Error('No active organization')
      return api.updateOrganization(activeOrg.id, orgName)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] })
      await refresh()
    },
  })

  // Build zone list: always include the user's current zone even if not in the preset list
  const tzOptions = COMMON_TIMEZONES.includes(currentTz)
    ? COMMON_TIMEZONES
    : [currentTz, ...COMMON_TIMEZONES]

  const tzMutation = useMutation({
    mutationFn: (tz: string) => api.updateTimezone(tz),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] })
      await refresh()
    },
  })

  const [selectedTz, setSelectedTz] = useState<string>(currentTz)

  // Keep the control synced with server/auth data without mutating state during render.
  useEffect(() => {
    if (!tzMutation.isPending) setSelectedTz(currentTz)
  }, [currentTz, tzMutation.isPending])

  function handleTzChange(tz: string) {
    setSelectedTz(tz)
    tzMutation.mutate(tz)
  }

  return (
    <section className="ds-view is-active" data-view="settings" data-screen-label="Настройки">
      <div className="ds-pagehead">
        <div>
          <h1>Настройки</h1>
          <p style={{ color: 'var(--ink-2)', marginTop: 4, fontSize: 14 }}>
            Управление аккаунтом и параметрами проектов
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 16, maxWidth: 560 }}>
        {/* Account card */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)',
          padding: '24px',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 16 }}>
            Аккаунт
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>Telegram ID</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--mono)' }}>
                {user?.telegram_user_id ?? '—'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>Тариф</span>
              <span
                className={`ds-tier__value ${user?.tier === 'trial' ? 'is-trial' : ''}`}
                style={{ fontSize: 16, fontWeight: 600 }}
              >
                {user?.tier ?? '—'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>Организация</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
                {activeOrg?.name ?? (user?.active_organization_id ? `Org ${user.active_organization_id}` : '—')}
              </span>
            </div>
          </div>

          {/* Active sessions */}
          {sessionsQuery.isSuccess && sessionsQuery.data.sessions.length > 0 && (
            <div className="sessions-block">
              <div className="sessions-block__head">
                <span className="sessions-block__title">
                  Активные сессии
                  <span className="sessions-block__count">
                    {sessionsQuery.data.sessions.length}
                  </span>
                </span>
                <span className="sessions-block__hint">
                  Все эти сессии сейчас активны. Каждая — отдельный вход с конкретного устройства. Сессия истекает через 7 дней или при нажатии «Завершить».
                </span>
              </div>
              <div className="sessions-list">
                {sessionsQuery.data.sessions.map((s: SessionItem) => {
                  const loginDate = new Date(s.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
                  const lastSeen = formatRelativeTime(s.last_seen_at)
                  const label = s.device_label ?? 'Неизвестное устройство'
                  return (
                    <div key={s.public_id} className={`session-row${s.current ? ' session-row--current' : ''}`}>
                      <div className="session-row__icon" aria-hidden="true">
                        {label.toLowerCase().includes('iphone') || label.toLowerCase().includes('ipad') || label.toLowerCase().includes('android')
                          ? '📱' : '💻'}
                      </div>
                      <div className="session-row__info">
                        <span className="session-row__label">{label}</span>
                        <span className="session-row__meta">
                          Вход {loginDate} · активность {lastSeen}
                        </span>
                      </div>
                      <div className="session-row__actions">
                        {s.current ? (
                          <span className="session-row__current-badge">это устройство</span>
                        ) : (
                          <button
                            type="button"
                            className="session-row__revoke"
                            disabled={revokeSessionMutation.isPending}
                            onClick={() => revokeSessionMutation.mutate(s.public_id)}
                            title="Завершить эту сессию"
                          >
                            Завершить
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={logoutAllMutation.isPending}
              onClick={() => logoutAllMutation.mutate()}
            >
              {logoutAllMutation.isPending ? 'Выходим…' : 'Выйти на всех устройствах'}
            </button>
            {logoutAllMutation.isError && (
              <span style={{ marginLeft: 8, fontSize: 13, color: 'oklch(0.55 0.18 25)' }}>
                Ошибка
              </span>
            )}
          </div>
        </div>

        {/* Organization card */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)',
          padding: '24px',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 16 }}>
            Компания
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            <input
              type="text"
              value={orgName}
              maxLength={80}
              disabled={!activeOrg || orgMutation.isPending}
              onChange={(e) => setOrgName(e.target.value)}
              style={{
                padding: '9px 12px',
                fontSize: 14,
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                color: 'var(--ink)',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                Это имя показывается в левом меню.
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {orgMutation.isSuccess && !orgMutation.isPending && (
                  <span style={{ fontSize: 13, color: 'var(--green, oklch(0.55 0.15 142))' }}>Сохранено</span>
                )}
                {orgMutation.isError && (
                  <span style={{ fontSize: 13, color: 'oklch(0.55 0.18 25)' }}>Ошибка</span>
                )}
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={!activeOrg || orgMutation.isPending || !orgName.trim() || orgName.trim() === activeOrg?.name}
                  onClick={() => orgMutation.mutate()}
                >
                  {orgMutation.isPending ? 'Сохраняем…' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Timezone card */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)',
          padding: '24px',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 16 }}>
            Часовой пояс
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, margin: 0 }}>
              Используется при выборе «Своего периода» при создании чата по источникам.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Select
                style={{ flex: 1 }}
                ariaLabel="Часовой пояс"
                value={selectedTz}
                onChange={(v) => handleTzChange(v)}
                disabled={tzMutation.isPending}
                options={tzOptions.map((tz) => ({ value: tz, label: tz }))}
              />
              {tzMutation.isPending && (
                <span style={{ fontSize: 13, color: 'var(--muted)', flexShrink: 0 }}>Сохраняем…</span>
              )}
              {tzMutation.isSuccess && !tzMutation.isPending && (
                <span style={{ fontSize: 13, color: 'var(--green, oklch(0.55 0.15 142))', flexShrink: 0 }}>Сохранено</span>
              )}
              {tzMutation.isError && (
                <span style={{ fontSize: 13, color: 'oklch(0.55 0.18 25)', flexShrink: 0 }}>Ошибка</span>
              )}
            </div>
          </div>
        </div>

        {/* Userbot card */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)',
          padding: '24px',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 16 }}>
            Юзербот (Telegram-аккаунт)
          </div>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5, margin: '0 0 14px' }}>
            Источники в «Чат по источникам» зависят от активного юзербота.
          </p>

          {userbotsQuery.isLoading && (
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Загрузка…</span>
          )}
          {userbotsQuery.isError && (
            <span style={{ fontSize: 13, color: 'oklch(0.55 0.18 25)' }}>Ошибка загрузки</span>
          )}
          {userbotsQuery.isSuccess && userbotsQuery.data.userbots.length === 0 && (
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>Нет доступных юзерботов</span>
          )}
          {userbotsQuery.isSuccess && userbotsQuery.data.userbots.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {userbotsQuery.data.userbots.map((ub) => {
                const isSelecting = selectingId === ub.id && selectUserbotMutation.isPending
                const only = userbotsQuery.data.userbots.length === 1
                return (
                  <div
                    key={ub.id}
                    onClick={() => !only && handleSelectUserbot(ub)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      borderRadius: 8,
                      border: `1px solid ${ub.is_active ? 'var(--accent, oklch(0.55 0.18 250))' : 'var(--border)'}`,
                      background: ub.is_active ? 'var(--accent-subtle, oklch(0.97 0.03 250))' : 'var(--bg, transparent)',
                      cursor: only || ub.is_active ? 'default' : 'pointer',
                      opacity: isSelecting ? 0.6 : 1,
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>
                        {ub.label}
                      </span>
                      {ub.telegram_user_id != null && (
                        <span style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                          ID: {ub.telegram_user_id}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      {isSelecting && (
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>Переключаем…</span>
                      )}
                      {selectUserbotMutation.isSuccess && selectingId === null && ub.is_active && !isSelecting && (
                        <span style={{ fontSize: 12, color: 'var(--green, oklch(0.55 0.15 142))' }}>Готово</span>
                      )}
                      {selectUserbotMutation.isError && selectingId === null && ub.is_active && !isSelecting && (
                        <span style={{ fontSize: 12, color: 'oklch(0.55 0.18 25)' }}>Ошибка</span>
                      )}
                      {ub.is_active && (
                        <span style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'var(--accent, oklch(0.55 0.18 250))',
                          background: 'var(--accent-subtle, oklch(0.97 0.03 250))',
                          border: '1px solid var(--accent, oklch(0.55 0.18 250))',
                          borderRadius: 4,
                          padding: '2px 7px',
                        }}>
                          активен
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
              {userbotsQuery.data.userbots.length === 1 && (
                <p style={{ fontSize: 12, color: 'var(--muted)', margin: '4px 0 0', lineHeight: 1.5 }}>
                  Единственный юзербот — переключение недоступно.
                </p>
              )}
            </div>
          )}

          <button
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ marginTop: 14 }}
            onClick={() => setConnectOpen(true)}
          >
            + Подключить юзербот
          </button>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '8px 0 0', lineHeight: 1.5 }}>
            Подключение через QR-код. Если на аккаунте включена 2FA — используйте Telegram-бота.
          </p>
        </div>

        {/* Bot settings card */}
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)',
          padding: '24px',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 16 }}>
            Управление проектами и источниками
          </div>
          <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.6, marginBottom: 16 }}>
            Создание проектов, добавление источников, настройка фильтров и ключевых слов выполняется через Telegram-бота.
          </p>
          <a
            href="https://t.me/sensor_tg_bot"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--primary btn--sm"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 14, height: 14, flexShrink: 0 }}>
              <path d="M9.78 18.65 9.99 15.42l5.83-5.26c.26-.23-.06-.34-.4-.14L8.21 14.5l-3.11-.97c-.67-.21-.68-.67.15-1L19.66 7c.56-.21 1.09.13.88.96l-2.7 12.74c-.18.85-.69 1.06-1.4.66l-3.81-2.82-1.84 1.78c-.21.21-.39.39-.81.39z" />
            </svg>
            Открыть @sensor_tg_bot
          </a>
        </div>
      </div>

      <ConnectUserbotModal
        isOpen={connectOpen}
        onClose={() => setConnectOpen(false)}
        onConnected={() => {
          void queryClient.invalidateQueries({ queryKey: ['me', 'userbots'] })
          void queryClient.invalidateQueries({ queryKey: ['chat', 'dialogs'] })
        }}
      />
    </section>
  )
}
